import { useMediaQuery, useTheme } from '@material-ui/core';
import queryString from 'query-string';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { ApplicantDetailOutcome } from 'hooks/useApplicantDetail';
import { usePostHog } from 'hooks/usePostHog';
import { removeParamsFromUrl, setParamsToUrl } from 'utils/urlUtils';

type EntryPoint = 'resume_link' | 'files_tab';
type ExitMethod = 'button' | 'escape' | 'drawer_close' | 'navigation';
interface DetailSettlement {
  applicantId: string;
  outcome: ApplicantDetailOutcome;
}
interface ResumeReviewContextValue {
  isFullPage: boolean;
  enter: (entryPoint: EntryPoint) => void;
  exit: (exitMethod: ExitMethod) => void;
  queue: null;
  go: () => void;
  detailSettlement: DetailSettlement | null;
  reportDetailSettled: (
    applicantId: string,
    outcome: ApplicantDetailOutcome,
  ) => void;
}

const noop = () => {};
const defaultResumeReview: ResumeReviewContextValue = {
  isFullPage: false,
  enter: noop,
  exit: noop,
  queue: null,
  go: noop,
  detailSettlement: null,
  reportDetailSettled: noop,
};
const ResumeReviewContext = createContext<ResumeReviewContextValue | null>(
  null,
);

export function ResumeReviewProvider({ children }: PropsWithChildren) {
  const flagOn = Boolean(
    useSelector(makeSelectWhoami()).feature_flags?.['applicant-resume-review'],
  );
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const { search } = useLocation();
  const params = queryString.parse(search);
  const isFullPage = flagOn && !mobileDevice && params.resumeReview === '1';
  const posthog = usePostHog();
  const reviewSessionId = useRef<string | null>(null);
  const activeReview = useRef(false);
  const mounted = useRef(false);
  const [detailSettlement, setDetailSettlement] =
    useState<DetailSettlement | null>(null);

  const captureClose = useCallback(
    (exitMethod: ExitMethod) => {
      if (!activeReview.current) return;
      activeReview.current = false;
      posthog.capture('applicants:resume_review_close', {
        exit_method: exitMethod,
        review_session_id: reviewSessionId.current,
      });
    },
    [posthog],
  );

  // URL restoration starts a session without attributing an invented entry point.
  useEffect(() => {
    if (isFullPage) {
      reviewSessionId.current ??= crypto.randomUUID();
      activeReview.current = true;
    } else if (params.persistedPopupType) {
      captureClose('navigation');
    }
  }, [isFullPage, params.persistedPopupType, captureClose]);

  // The close saga clears the URL before GlobalPopup unmounts this provider.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Strict Mode reconnects effects synchronously without closing the drawer.
      void Promise.resolve().then(() => {
        if (!mounted.current) captureClose('drawer_close');
      });
    };
  }, [captureClose]);

  const enter = useCallback(
    (entryPoint: EntryPoint) => {
      if (!flagOn || mobileDevice || activeReview.current) return;
      reviewSessionId.current ??= crypto.randomUUID();
      activeReview.current = true;
      setParamsToUrl({ resumeReview: '1' });
      posthog.capture('applicants:resume_review_open', {
        entry_point: entryPoint,
        review_session_id: reviewSessionId.current,
      });
    },
    [flagOn, mobileDevice, posthog],
  );
  const exit = useCallback(
    (exitMethod: ExitMethod) => {
      captureClose(exitMethod);
      removeParamsFromUrl({ resumeReview: '' });
    },
    [captureClose],
  );
  const reportDetailSettled = useCallback(
    (applicantId: string, outcome: ApplicantDetailOutcome) => {
      setDetailSettlement({ applicantId, outcome });
    },
    [],
  );
  const value = useMemo(
    () => ({
      isFullPage,
      enter,
      exit,
      queue: null,
      go: noop,
      detailSettlement,
      reportDetailSettled,
    }),
    [isFullPage, enter, exit, detailSettlement, reportDetailSettled],
  );

  return (
    <ResumeReviewContext.Provider value={value}>
      {children}
    </ResumeReviewContext.Provider>
  );
}

export function useResumeReview() {
  return useContext(ResumeReviewContext) ?? defaultResumeReview;
}

export function useOptionalResumeReview() {
  return useContext(ResumeReviewContext);
}
